import { SpinnerLoader } from '@assets/icons'
import { HeaderPage } from '@components/index'
import { REAL_ESTATE_TYPE } from '@interfaces/realEstates/enums/realEstateType'
import { IRealEstateSummery } from '@interfaces/realEstates/iRealEstate'
import { ICreateTableSales, ITableSales, ITemplate } from '@interfaces/tables/tableSales'
import { errorHandler } from '@services/index'
import RealEstateService from '@services/requests/realEstateService'
import TableSalesService from '@services/requests/tableSalesService'
import { formatDate, setDate } from '@utils/dates/dateUtils'
import React, { useTransition } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import TableSales from './TableSales'

export default function FormTableSales(): React.ReactElement {
    const navigate = useNavigate()
    const [realEstates, setRealEstates] = React.useState<IRealEstateSummery[]>([])
    const [data, setData] = React.useState<ITableSales>()
    const [isLoading, setIsLoading] = React.useState(false)

    const params = useParams()
    const queryPage = useLocation().search

    async function getTemplate(): Promise<ITemplate | undefined> {
        setIsLoading(true)
        try {
            if (params.id) {
                return await TableSalesService.getTemplate(parseInt(params.id))
            }
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoading(false)
        }
    }

    async function submit(values: ICreateTableSales): Promise<void> {
        setIsLoading(true)
        const formData = new FormData()
        if (values.tableDate.match(/\d{4}-\d{2}/)) {
            const [year, month] = values.tableDate.split('-')
            const parsedDate = setDate(new Date(), {
                year: parseInt(year),
                month: parseInt(month) - 1
            })
            formData.append('tableDate', formatDate(parsedDate, 'yyyy-MM'))
        } else {
            formData.append('tableDate', formatDate(new Date(values.tableDate), 'yyyy-MM'))
        }
        formData.append('realEstateId', values.realEstateId)
        formData.append('name', values.name)
        if (values.file) {
            formData.append('file', values.file as File)
        } else if (values.removeTemplate) {
            formData.append('removeTemplate', JSON.stringify(values.removeTemplate))
        }
        values.rows.forEach(row => {
            delete row.id
            row.cells?.forEach(cell => {
                if (cell.value === 'none')
                    cell.value = ''
            })
        })
        values.columns.forEach(column => {
            delete column.columnId
        })
        formData.append('columns', JSON.stringify(values.columns))
        formData.append('rows', JSON.stringify(values.rows))

        try {
            if (params.id) {
                await TableSalesService.update(formData, parseInt(params.id))
            } else {
                await TableSalesService.create(formData)
            }
            toast('Tabela salva com sucesso!', { type: 'success' })
            navigate('/tabela-de-vendas')
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoading(false)
        }
    }

    const [isPending, startTransition] = useTransition()

    async function init(): Promise<void> {
        setIsLoading(true)
        try {
            const realEstates = await RealEstateService.summary([REAL_ESTATE_TYPE.RELEASE, REAL_ESTATE_TYPE.VENTURE, REAL_ESTATE_TYPE.COMMING_SOON])
            if (params.id) {
                const tableSales = await TableSalesService.getOne(parseInt(params.id))
                tableSales.rows.forEach(element => {
                    delete element.rowNumber
                    element.cells?.forEach(cell => {
                        delete cell.id
                    })
                })
                if (tableSales.realEstate && tableSales.realEstateId && !realEstates.find(r => r.id === tableSales.realEstateId)) {
                    realEstates.unshift({
                        id: tableSales.realEstate?.id,
                        name: tableSales.realEstate?.name
                    })
                }
                startTransition(() => {
                    setRealEstates(realEstates)
                    setData(tableSales)
                })
            } else {
                setRealEstates(realEstates)
                setData({
                    id: 1,
                    realEstateId: undefined,
                    tableDate: (new Date()).toString(),
                    name: '',
                    rows: [{
                        cells: [{ value: '', columnId: 0 }]
                    }],
                    columns: [
                        {
                            columnId: 0,
                            name: 'Unidade'
                        },
                        {
                            columnId: 1,
                            name: 'Status'
                        },
                        {
                            columnId: 2,
                            name: 'Valor'
                        },
                    ]
                })
            }
        } catch (error) {
            toast('Ocorreu um problema ao buscar os dados', { type: 'error' })
        } finally {
            setIsLoading(false)
        }
    }

    React.useEffect(() => {
        init()
    }, [])

    return (
        <div className="card">
            <div className="create-table-sales">
                {isLoading || isPending ? (
                    <div className="loader-page">
                        <SpinnerLoader />
                    </div>
                ) : (
                    <></>
                )}
                <HeaderPage title={params.id ? 'Editar Tabela de Vendas' : 'Nova Tabela de Vendas'} />
                <TableSales
                    data={data}
                    realEstates={realEstates}
                    pageThatWas={queryPage}
                    submit={submit}
                    getTemplate={() => getTemplate()}
                    params={params}
                />
            </div>
        </div>
    )
}
